import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { currentIdToken } from '../auth/cognito';
import { useGameSocket } from '../ws/useGameSocket';
import { useGameStore } from '../store';
import { useCountdown } from '../hooks/useCountdown';
import { Action } from '../shared/protocol';
import { JoinQR } from '../components/JoinQR';

export function AdminConsole() {
  const { gameId } = useParams<{ gameId: string }>();
  const navigate = useNavigate();
  const [token, setToken] = useState<string | null>(null);

  useEffect(() => {
    void currentIdToken().then((t) => {
      if (!t) navigate('/admin', { replace: true });
      else setToken(t);
    });
  }, [navigate]);

  const socket = useGameSocket(token && gameId ? { role: 'admin', token, gameId } : null);
  const { connected, state, calledOn, lastResult, error } = useGameStore();
  const open = state?.questionState === 'open';
  const remaining = useCountdown(open ? state?.endsAt : undefined);

  const [showQR, setShowQR] = useState(true);
  const [copied, setCopied] = useState(false);

  const send = (action: string, data?: unknown) => socket?.send(action, data);

  const joinLink = state ? `${window.location.origin}/play/${state.code}` : '';
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(joinLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard blocked — the link is shown next to the button anyway */
    }
  };

  return (
    <div className="stack">
      <div className="rowbetween">
        <div className="row">
          <Link to="/admin/games" className="ghost">← Games</Link>
          {state && <span className="pill">Code: <strong>{state.code}</strong></span>}
        </div>
        <span className={connected ? 'status ok' : 'status bad'}>{connected ? 'live' : 'connecting…'}</span>
      </div>

      {error && <div className="banner error">{error}</div>}
      {!state && <div className="card centered"><p className="muted">Loading console…</p></div>}

      {state && state.status !== 'finished' && (
        <div className="card">
          <div className="rowbetween">
            <span>Join: <a href={joinLink} className="joinlink">{joinLink.replace(/^https?:\/\//, '')}</a></span>
            <span className="row">
              <button className="ghost small" onClick={copyLink}>{copied ? '✓ Copied' : 'Copy link'}</button>
              <button className="ghost small" onClick={() => setShowQR((v) => !v)}>{showQR ? 'Hide QR' : 'Show QR'}</button>
            </span>
          </div>
          {showQR && <JoinQR code={state.code} />}
        </div>
      )}

      {state && (
        <>
          {state.status === 'lobby' && (
            <div className="card centered">
              <h2>Waiting for players…</h2>
              <p className="muted">Press <strong>Start game</strong> when everyone's in.</p>
            </div>
          )}

          <div className="card">
            <div className="rowbetween">
              <div className="muted small">{state.topicTitle ?? '—'}</div>
              <div className="muted small">state: {state.status} / {state.questionState}</div>
            </div>

            {state.question ? (
              <>
                <h2 className="question">{state.question.text}</h2>
                <div className="rowbetween">
                  <span className="points">{state.question.points} pts</span>
                  {open && <span className="timer">{remaining}s</span>}
                </div>
                {state.answerNote && <div className="answer">Answer: {state.answerNote}</div>}
              </>
            ) : (
              <p className="muted">No question selected.</p>
            )}

            <div className="controls">
              {state.status === 'lobby' && (
                <button className="primary" onClick={() => send(Action.AdminStart)}>Start game</button>
              )}
              {state.status === 'active' && (
                <>
                  {/* Buzzer only opens for a fresh (idle) question. Once it has run
                      or been answered, it must be explicitly reset to run again. */}
                  {state.question && state.questionState === 'idle' && (
                    <button className="primary" onClick={() => send(Action.AdminOpenQ)}>
                      Open buzzer (10s)
                    </button>
                  )}
                  {state.question && state.questionState !== 'idle' && (
                    <button className="primary" onClick={() => send(Action.AdminResetQ)}>
                      🔄 Reset buzzer
                    </button>
                  )}
                  <span className="navgroup">
                    <button className="ghost" title="Previous topic" onClick={() => send(Action.AdminPrevTop)}>◀◀</button>
                    <button className="ghost" title="Previous question" onClick={() => send(Action.AdminPrevQ)}>◀</button>
                    <button className="ghost" title="Next question" onClick={() => send(Action.AdminNextQ)}>▶</button>
                    <button className="ghost" title="Next topic" onClick={() => send(Action.AdminNextTop)}>▶▶</button>
                  </span>
                  <button className="danger" onClick={() => { if (confirm('End the game?')) send(Action.AdminEndGame); }}>
                    End game
                  </button>
                </>
              )}
              {state.status === 'finished' && <p className="muted">Game finished.</p>}
            </div>
          </div>

          {state.status === 'active' && (
            <div className="card">
              <h3>Buzzed in ({state.queue?.length ?? 0})</h3>
              {(!state.queue || state.queue.length === 0) ? (
                <p className="muted">No buzzes yet.</p>
              ) : (
                <ol className="buzzqueue">
                  {state.queue.map((e) => (
                    <li key={e.playerId} className={`res-${e.result}`}>
                      <span className="qname">
                        {calledOn?.playerId === e.playerId && '📣 '}
                        {e.nickname}
                        {e.result !== 'pending' && <em> — {e.result}</em>}
                      </span>
                      <span className="row">
                        <button className="ghost" onClick={() => send(Action.AdminCallOn, { playerId: e.playerId })}>Call on</button>
                        <button className="ok" onClick={() => send(Action.AdminMark, { playerId: e.playerId, correct: true })}>✓</button>
                        <button className="danger" onClick={() => send(Action.AdminMark, { playerId: e.playerId, correct: false })}>✗</button>
                      </span>
                    </li>
                  ))}
                </ol>
              )}
              {lastResult && (
                <div className={`banner ${lastResult.correct ? 'success' : 'error'}`}>
                  {lastResult.nickname}: {lastResult.correct ? `correct (+${lastResult.points})` : 'wrong'}
                </div>
              )}
            </div>
          )}

          <div className="card">
            <h3>Scores</h3>
            <ul className="scores">
              {(state.scores ?? []).map((s) => (
                <li key={s.playerId}><span>{s.nickname}</span><span className="points">{s.score}</span></li>
              ))}
              {(state.scores ?? []).length === 0 && <li className="muted">No players yet.</li>}
            </ul>
          </div>
        </>
      )}
    </div>
  );
}
